import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { COUNTRY } from '../const/constants'
import { useAppContext } from '../context/AppContext'
import { CLEAR_SEARCH } from '../redux/actions'
import { appServicePaises } from '../services/api/nominatividad'
import { getLocalidades, getProvincias } from '../services/api/normalizador'
import { refCodesParameters, stlParameters } from '../services/api/settings'
import { parameterizeDate } from '../utils/utils'
import useMessages from './useMessages'
import useRequests from './useRequests'

interface Provincia {
  geuDescription: string
  geuId: string
  geuCpaPrefix: null
}

interface Localidad {
  localidad: string
  ciudad: string
}

const today = new Date().toISOString()

const useFormFields = () => {
  const { settings, user }: any = useAppContext()
  const [provincias, setProvincias]: any = useState([])
  const [localidades, setLocalidades]: any = useState([])
  const [sex, setSex]: any = useState([])
  const [nomDays, setNomDays] = useState<number>(0)
  const [countries, setCountries]: any = useState([])
  const [disableField, setDisableField]: any = useState(false)
  const dispatch = useDispatch()

  const productInput = useSelector((state: any) => state.nuevoProspectoForm.negocio)

  const { searchClient } = useRequests('idNominativityForm')

  const searchClients = async (input: string) => {
    const response = await searchClient(input)
    setDisableField(response.isClt)
  }

  const clearSearch = () => {
    dispatch({ type: CLEAR_SEARCH })
    setDisableField(false)
  }

  const { toastMessage } = useMessages()

  const loadProvinces = async () => {
    try {
      const response = await getProvincias()
      setProvincias(response)
      return response
    } catch (error) {
      toastMessage('Error de servidor, intentá nuevamente', 2000, 'bottom', 'error')
    }
  }

  const loadParameters = async () => {
    try {
      const refCodeResponse = await refCodesParameters({
        rvDomain: 'ASM_SEXO',
        rvLowValue: 'EXT'
      })
      const genders = refCodeResponse.filter((gender: any) => gender.btDefault === 'Y')
      setSex(genders)
      const stlResponse = await stlParameters({
        stlIds: ['NOMDAY']
      })
      setNomDays(Number(stlResponse[0].intValues))
    } catch (error) {
      toastMessage('Error de servidor, intentá nuevamente', 2000, 'bottom', 'error')
    }
  }

  const loadCountries = async () => {
    try {
      const response = await appServicePaises()
      setCountries(response)
      return response
    } catch (error) {
      toastMessage('Error de servidor, intentá nuevamente', 2000, 'bottom', 'error')
    }
  }

  const loadLocalities = async (provincia: string) => {
    try {
      const response = await getLocalidades(provincia)
      const hash: any = {}
      const dataArr: any = response.filter((item: any) => {
        const exists = !hash[item.localidad]
        hash[item.localidad] = true
        return exists
      })
      setLocalidades(dataArr)
      return response
    } catch (error) {
      toastMessage('Error de servidor, intentá nuevamente', 2000, 'bottom', 'error')
    }
  }

  useEffect(() => {
    ;(async function () {
      if (settings.country === COUNTRY.AR) await Promise.all([loadProvinces(), loadParameters(), loadCountries()])
    })()
  }, [])

  const loadOptions = async (provincia: string) => await loadLocalities(provincia)

  const evaluateProduct = () => {
    return settings.country === COUNTRY.AR && productInput === '1'
  }

  const nuevoProspectoFields = [
    {
      type: 'text',
      label: 'Nombre/s',
      inputName: 'nombre',
      inputMode: 'text',
      min: settings.validation.namesMinLength,
      max: settings.validation.namesMaxLength
    },
    {
      type: 'text',
      label: 'Apellido/s',
      inputName: 'apellido',
      inputMode: 'text',
      min: settings.validation.namesMinLength,
      max: settings.validation.namesMaxLength
    },
    {
      type: 'select',
      label: 'Negocio / Producto',
      inputName: 'negocio',
      options: settings.options.referred.businessType
    },
    {
      type: 'text',
      label: evaluateProduct() ? 'Número de documento (Opcional)' : 'Número de documento',
      inputName: 'documento',
      inputMode: 'numeric',
      min: settings.validation.documentMinLength,
      max: settings.validation.documentMaxLength,
      note: 'CI / DNI / EXT.',
      optional: evaluateProduct()
    },
    {
      type: 'text',
      label: 'N° telefónico a portar o de contacto',
      inputName: 'telefono',
      inputMode: 'tel',
      min: settings.validation.lineNumberLength,
      max: settings.validation.lineNumberLength,
      note:
        settings.country === COUNTRY.PY
          ? 'Ingresá el número sin el 0 al inicio.'
          : 'Ingresá el número sin el 0 al inicio y sin el 15.'
    },
    {
      type: 'text',
      label: 'Email de contacto',
      inputName: 'email',
      inputMode: 'email',
      min: settings.validation.emailMinLength,
      max: settings.validation.emailMaxLength
    }
  ]

  const lineValidationFieldsPY = [
    {
      type: 'tel',
      label: 'Número de línea a portar',
      inputName: 'lineNumber',
      inputMode: 'tel',
      min: settings.validation.lineNumberLength,
      max: settings.validation.lineNumberLength,
      note: settings.country === COUNTRY.PY ? 'Ingresalo sin el 0.' : 'Ingresalo sin el 0 y sin el 15.'
    },
    {
      type: 'select',
      label: 'Tipo de servicio actual',
      inputName: 'serviceType',
      options: settings.options.portability.serviceType
    },
    {
      type: 'text',
      label: 'Correo electrónico (Opcional)',
      inputName: 'emailOptional',
      inputMode: 'email',
      min: settings.validation.emailMinLength,
      max: settings.validation.emailMaxLength,
      note: 'Recordale al cliente que puede retirar su documentación en un Centro de Atención de Claro.',
      largeNote: true,
      optional: true
    },
    {
      type: 'number',
      label: 'Número de chip',
      inputName: 'cardNumber',
      inputMode: 'numeric',
      min: settings.validation.simNumberMinLength,
      max: settings.validation.simNumberMaxLength
    },
    {
      type: 'scanBarCodeComponent',
      label: 'ESCANEAR CÓDIGO DEL CHIP',
      inputName: 'cardNumber'
    },
    {
      type: 'text',
      label: 'Número de POS',
      inputName: 'posNumberProcedure',
      inputMode: 'text',
      disabled: Boolean(user.posActivator),
      min: settings.validation.posNumberMinLength,
      max: settings.validation.posNumberMaxLength
    }
  ]

  const chipValidationFields = [
    {
      type: 'number',
      label: 'Número de chip',
      inputName: 'cardNumber',
      inputMode: 'numeric',
      min: settings.validation.simNumberMinLength,
      max: settings.validation.simNumberMaxLength
    },
    {
      type: 'scanBarCodeComponent',
      label: 'ESCANEAR CÓDIGO DEL CHIP',
      inputName: 'cardNumber'
    },
    {
      type: 'number',
      label: 'Número de POS',
      inputName: 'posNumberProcedure',
      inputMode: 'numeric',
      disabled: Boolean(user.posActivator),
      min: settings.validation.posNumberMinLength,
      max: settings.validation.posNumberMaxLength
    }
  ]

  const preOBDFields = [
    {
      type: 'select',
      label: 'Tipo de documento',
      inputName: 'documentType',
      options: settings.options.identity.documentType,
      defaultValue: settings.defaultValues.documentType // esta es la ultima linea de código de braiansoft en kiosquito
    },
    {
      type: 'number',
      label: 'Número del documento',
      inputName: 'documentNumber',
      inputMode: 'text',
      min: settings.validation.documentMinLength,
      max: settings.validation.documentMaxLength
    }
  ]

  const idNominativityFields = [
    {
      type: 'text',
      label: 'Número del documento',
      inputName: 'passport',
      inputMode: 'text',
      min: 5,
      max: 15,
      note: 'Puede ser DNI Extranjero, CI o Pasaporte.',
      searchInput: true,
      searchFunction: searchClients,
      clearFunction: clearSearch
    },
    {
      type: 'text',
      label: 'Nombre/s',
      inputName: 'names',
      inputMode: 'text',
      min: settings.validation.documentMinLength,
      max: settings.validation.extMaxLength,
      disabled: disableField
    },
    {
      type: 'text',
      label: 'Apellido/s',
      inputName: 'lastName',
      inputMode: 'text',
      min: settings.validation.namesMinLength,
      max: settings.validation.namesMaxLength,
      disabled: disableField
    },
    {
      type: 'select',
      label: 'Sexo',
      inputName: 'sex',
      options: sex.map((value: any) => ({
        label: value.abbreviation,
        value: value.highValue
      })),
      disabled: disableField
    },
    {
      type: 'date',
      flow: 'nom',
      label: 'Fecha de nacimiento',
      inputName: 'birthDate',
      maxLimitDate: today,
      disabled: disableField
    },
    {
      type: 'selectSearch',
      label: 'Nacionalidad',
      inputName: 'nationality',
      options: countries?.map(({ ctrName, ctrId }: any) => ({
        label: ctrName,
        value: ctrId
      })) // traer de la tabla los paises
    },
    {
      type: 'date',
      flow: 'nom',
      label: 'Fecha límite de permanencia',
      inputName: 'limitDate',
      minLimitDate: today,
      maxLimitDate: parameterizeDate(nomDays), // setear máximo de limite de permanencia por parámetro
      defaultDate: parameterizeDate(15) // setear fecha por defecto y ver si es conveniente igual que limite o default de 15 dias
    }
  ]

  const addressFields = [
    {
      type: 'selectSearch',
      label: 'Provincia',
      inputName: 'geuDescription',
      options: provincias?.map(({ geuDescription, geuId }: Provincia) => ({
        label: geuDescription,
        value: geuDescription,
        id: geuId
      })),
      setSelected: loadOptions
    },
    {
      type: 'selectSearch',
      label: 'Ciudad',
      inputName: 'accAddressCity',
      options: localidades?.map(({ localidad }: Localidad) => ({
        label: localidad,
        value: localidad
      })),
      disabled: !localidades.length
    },
    {
      type: 'text',
      label: 'Calle',
      inputName: 'addressStreet',
      inputMode: 'text',
      min: settings.validation.namesMinLength,
      max: settings.validation.namesMaxLength
    },
    {
      type: 'number',
      label: 'Número',
      inputName: 'addressNumber',
      inputMode: 'number',
      min: settings.validation.addressNumberMinLength,
      max: settings.validation.addressNumberMaxLength
    },
    {
      type: 'number',
      label: 'Piso',
      inputName: 'addressFloor',
      inputMode: 'number',
      min: settings.validation.addressFloorMinLength,
      max: settings.validation.addressFloorMaxLength,
      shortInput: true,
      optional: true
    },
    {
      type: 'text',
      label: 'Dpto',
      inputName: 'addressFlat',
      inputMode: 'text',
      min: settings.validation.addressFlatMinLength,
      max: settings.validation.addressFlatMaxLength,
      shortInput: true,
      optional: true
    }
  ]
  return {
    nuevoProspectoFields,
    lineValidationFieldsPY,
    preOBDFields,
    chipValidationFields,
    idNominativityFields,
    addressFields
  }
}

export default useFormFields
